/**
 * 금액 입력 — 사칙연산 수식 평가기 (안전, eval 미사용).
 *
 * 허용:
 *   - 숫자 (소수점 포함), 공백, 콤마 (1,000 등 표기 허용)
 *   - +, -, *, /, ( )
 *   - 단항 +/- (예: -5, -(2+3))
 *
 * 거부:
 *   - 알파벳·식별자·따옴표·대괄호·중괄호 등 (정규식으로 사전 차단)
 *   - 0 나누기 (null 반환)
 *   - 불완전한 식 (예: "1+", "(2+3", "1++2")
 *
 * 반환: 평가된 number. 실패 시 null.
 *
 * 사용:
 *   evalAmount('=150*2')          // 300
 *   evalAmount('=(100+50)*2')     // 300
 *   evalAmount('=1,000 + 500')    // 1500
 *   evalAmount('150*2')           // null  ← '=' prefix 필수 아님 — 호출 측에서 strip 후 전달
 *   evalAmount('abc')             // null
 *
 * 호출 측에서 `=` prefix 를 먼저 떼고 본 함수에 식만 넘긴다.
 */

/** 표현식 문자열을 평가. 실패 시 null. */
export function evalAmount(expr: string): number | null {
  const src = expr.replace(/[\s,]/g, '');
  if (src === '') return null;
  if (!/^[0-9+\-*/().]+$/.test(src)) return null;

  let pos = 0;

  function peek(): string {
    return src[pos] ?? '';
  }
  function consume(): string {
    return src[pos++] ?? '';
  }

  function parseNumber(): number | null {
    const start = pos;
    while (pos < src.length && /[0-9.]/.test(src[pos]!)) pos++;
    if (start === pos) return null;
    const slice = src.slice(start, pos);
    // 소수점 두 번 등 부정확한 형태 거부
    if (slice.split('.').length > 2) return null;
    const n = parseFloat(slice);
    return Number.isFinite(n) ? n : null;
  }

  function parseFactor(): number | null {
    let sign = 1;
    while (peek() === '+' || peek() === '-') {
      if (consume() === '-') sign = -sign;
    }
    if (peek() === '(') {
      consume();
      const v = parseExpr();
      if (v === null) return null;
      if (peek() !== ')') return null;
      consume();
      return sign * v;
    }
    const n = parseNumber();
    return n === null ? null : sign * n;
  }

  function parseTerm(): number | null {
    let v = parseFactor();
    if (v === null) return null;
    while (peek() === '*' || peek() === '/') {
      const op = consume();
      const r = parseFactor();
      if (r === null) return null;
      if (op === '/' && r === 0) return null;
      v = op === '*' ? v * r : v / r;
    }
    return v;
  }

  function parseExpr(): number | null {
    let v = parseTerm();
    if (v === null) return null;
    while (peek() === '+' || peek() === '-') {
      const op = consume();
      const r = parseTerm();
      if (r === null) return null;
      v = op === '+' ? v + r : v - r;
    }
    return v;
  }

  const result = parseExpr();
  if (pos !== src.length) return null;
  if (result === null || !Number.isFinite(result)) return null;
  return result;
}

/**
 * 금액 입력 문자열 → 숫자 문자열 변환.
 *   - "=150*2"          → "300"
 *   - "1,000"           → "1000"
 *   - "300"             → "300"
 *   - "abc"             → null
 *   - "=18525000/1.1" + decimals=2 → "16840909.09"  (소수 보존)
 *
 * decimals: 확정값 소수 자리수 (기본 0 = 원 단위 반올림 — 기존 동작 유지).
 *   단가처럼 DB 가 소수를 저장(numeric(15,2))하는 칸은 2 를 넘겨 계산 정밀도 보존.
 */
export function normalizeAmountInput(text: string, decimals = 0): string | null {
  const pow = 10 ** Math.max(0, Math.min(6, Math.trunc(decimals)));
  // 부동소수 오차 방지 — 지정 자리 반올림. 음수 허용은 호출 측 판단.
  const rnd = (x: number) => Math.round(x * pow) / pow;
  const trimmed = text.trim();
  if (trimmed === '') return '0';
  if (trimmed.startsWith('=')) {
    const result = evalAmount(trimmed.slice(1));
    if (result === null) return null;
    return String(rnd(result));
  }
  const cleaned = trimmed.replace(/[\s,]/g, '');
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null;
  const n = rnd(parseFloat(cleaned));
  return Number.isFinite(n) ? String(n) : null;
}
